// src/com/file_create_his/FileCreateHisDownload.js
import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, DialogActions, DialogContent } from '@mui/material';
import { saveAs } from 'file-saver'; // 엑셀로 전환된 것을 받은 후 저장하기 위함
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import DoneIcon from '@mui/icons-material/Done';
import DraggableViewer from '../../common/DraggableViewer';
import { AppObject, AppStyle } from '../../value/AppConst';
import { useLazyQuery } from '@apollo/client';
import { GET_FILE_CREATE_HIS_URL } from './FileCreateHisGql';
import ExcelBuilder from '../file_of_report/ExcelBuilder';

const ViewContainer = styled(Box)({
    marginTop: 10, minWidth: 800, minHeight: 100
});

const ProgressContainer = styled(Box)({
    marginTop: 20, marginBottom: 20, height: 40, display: 'flex', alignItems: 'center', padding: 40
});

const ProgressBox = styled(Box)({
    flexGrow: 1, borderRadius: 5, backgroundColor: '#e0e0e0', overflow: 'hidden'
});

const ProgressBar = styled(Box)({
    height: '100%', backgroundColor: AppStyle.PrimaryRGB, borderRadius: 5, transition: 'width 0.3s ease'
});

/**
 * 주어진 아이템의 파일을 다운로드.
 * 서버에 fileCreateHisSeq, serverUrl를 보내면 fileAddr에 pre-signed URL이 생성되어 돌아온다.
 * 그 URL로 접속하여 파일을 다운로드한다.
 * fileExt가 json인 경우는 disposition을 'inline'으로 하여 받은 후 내용을 excel로 변환하여 저장한다.
 * 그 외의 경우는 disposition을 'attachment'로 하여 바로 다운로드한다.
 * @param {*} param0 
 * @returns 
 */
export default function FileCreateHisDownload({
    open,
    item, // FileCreateHis {serverUrl, fileAddr, fileCreateHisSeq, fileExt}
    onClose,
    getExtraHeaders, // function(records): string
    columns, // Excel column definitions
    instructions // {columns:[], headerRowSpan: 3, title: '', headerRows: []}
}) {
    const [message, setMessage] = useState('파일 다운로드를 준비 중입니다...');
    const [linkInfo, setLinkInfo] = useState(null); // {url, fileName}
    const [progress, setProgress] = useState(0); // 0~100 (Excel 변환용)
    const [fetching, setFetching] = useState(false);
    const openRef = useRef(false);
    // Call GraphQL to get pre-signed URL
    const [getPresignedUrl, responseGetUrl] = useLazyQuery(GET_FILE_CREATE_HIS_URL, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetPresignedUrl(data, option)},
        onError: (error) => setMessage('파일 다운로드 정보를 가져오지 못했습니다: ' + error.message)
    });

    useEffect(()=>{
        if(open && !openRef.current) {
            if(item) {
                if(item.fileCreateHisSeq && item.serverUrl) {
                    setMessage('파일 다운로드가 가능한지 확인 중입니다...');
                    openRef.current = true;
                    const param = {fileCreateHisSeq: item.fileCreateHisSeq, serverUrl: item.serverUrl};
                    getPresignedUrl({variables: param});
                }
                else {
                    setMessage('파일 다운로드 정보가 올바르지 않습니다.');
                }
            }
            else {
                setMessage('파일 다운로드 정보가 제공되지 않았습니다.');
            }
        }
        else openRef.current = false;
    }, [open, item, getPresignedUrl]);

    const saveExcel = async (excelBuilder, fileName) => {
        saveAs(await excelBuilder.getBlob(), fileName);
        setMessage('위치 및 이름을 선택하여 다운로드를 완료하세요.');
    };

    const onCompleteGetPresignedUrl = (data, option) => {
        if(data && data.fileCreateHisUrl.ok) {
            const url = data.fileCreateHisUrl.item.fileAddr;
            setMessage('파일 다운로드를 시작합니다...');
            if(item.fileExt === 'json') {
                // use fetch to download
                setFetching(true);
                fetch(url)
                    .then(response => response.json())
                    .then(records => {
                        setMessage('파일을 엑셀로 변환 중입니다...');
                        setFetching(false);

                        const excelInstructions = {...instructions}; // {title, headerRowSpan, headerRows}
                        excelInstructions.columns = columns;
                        const extraHeaders = getExtraHeaders ? getExtraHeaders(records) : [];
                        if(!excelInstructions.headerRows) excelInstructions.headerRows = [];
                        excelInstructions.headerRows.push(...extraHeaders);

                        const excelBuilder = new ExcelBuilder(excelInstructions, records);
                        excelBuilder.build(setProgress);
                        const fileName = item.fileName ? item.fileName.replace(/\.json$/i, '.xlsx') : `report.xlsx`;
                        saveExcel(excelBuilder, fileName);
                        onClose();
                    })
                    .catch(error => {
                        setMessage('파일 다운로드 중 오류가 발생했습니다: ' + error.message);
                    });
            }
            else {
                // direct download
                const fileName = item.fileName || `downloaded_file.${item.fileExt || 'dat'}`;
                setLinkInfo({url: url, fileName: fileName});
                setMessage('다운로드가 시작되지 않으면 아래 링크를 클릭하여 파일을 다운로드하십시오.');
                saveAs(url, fileName);
            }
        }
        else {
            setMessage('파일 다운로드 정보를 가져오지 못했습니다: ' + (data.fileCreateHisUrl.message || ''));
        }
    };

    const loadingAny = fetching || responseGetUrl.loading;

    return(
        <DraggableViewer open={open} onClose={onClose} maxWidth='md'
            startIcon={<SimCardDownloadIcon fontSize="large" />}
            title={`파일 다운로드`}
        >
            <DialogContent>
                <ViewContainer>
                    <Box sx={{marginTop:2, marginBottom:2, textAlign:'center', fontSize:'0.9rem'}}>
                        {message}
                    </Box>
                    <ProgressContainer>
                        {
                            linkInfo ?
                            <ProgressBox>
                                <a href={linkInfo.url} download={linkInfo.fileName}>
                                    다운로드 {linkInfo.fileName}
                                </a>
                            </ProgressBox>
                            :
                            <ProgressBox>
                                {progress > 0 && <ProgressBar sx={{width: `${progress}%`}} />}
                            </ProgressBox>}
                    </ProgressContainer>
                </ViewContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    sx={{width: 120}}
                    startIcon={<DoneIcon />}
                    disabled={loadingAny}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableViewer>
    );
}
